// Copyright (c) 2025-2026 Motswagae Holding (Pty) Ltd. All Rights Reserved.
// CIPC Registration: 2019/443098/07
// Author: Velly Sipho Phaloane
//
// This source code is the proprietary and confidential property of
// Motswagae Holding (Pty) Ltd, trading as 360Wallet.
// Unauthorized copying, modification, distribution, or use of this
// software, in whole or in part, is strictly prohibited.
//
// Protected under the Copyright Act 98 of 1978 (South Africa)
// and international copyright treaties.
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { getSettlementLedger, getSettlementSummary, settleEntry, bulkSettle, getBanks, getRetailStores } from "../lib/api";
import { toast } from "sonner";
import { 
  Building2, 
  Store, 
  Network, 
  Wallet,
  ArrowDownRight,
  ArrowUpRight,
  CheckCircle2,
  Clock,
  RefreshCw,
  ChevronRight,
  DollarSign,
  TrendingUp,
  TrendingDown,
  FileText,
  Download
} from "lucide-react";

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return "—";
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    minimumFractionDigits: 2
  }).format(amount);
};

const formatDate = (dateString) => {
  if (!dateString) return "—";
  return new Date(dateString).toLocaleDateString('en-ZA', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const formatDateTime = (dateString) => {
  if (!dateString) return "—";
  return new Date(dateString).toLocaleString('en-ZA', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const PartyTypeIcon = ({ type }) => {
  const icons = {
    bank: Building2,
    retailer: Store,
    atm_acquirer: Network,
    platform: Wallet
  };
  const Icon = icons[type] || Building2;
  return <Icon className="h-4 w-4" />;
};

const PartyTypeBadge = ({ type }) => {
  const styles = {
    bank: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    retailer: "bg-purple-500/20 text-purple-400 border-purple-500/30",
    atm_acquirer: "bg-orange-500/20 text-orange-400 border-orange-500/30",
    platform: "bg-green-500/20 text-green-400 border-green-500/30"
  };
  const labels = {
    bank: "Bank",
    retailer: "Retailer",
    atm_acquirer: "ATM Acquirer",
    platform: "Platform"
  };
  
  return (
    <Badge variant="outline" className={styles[type] || styles.bank}>
      <PartyTypeIcon type={type} />
      <span className="ml-1">{labels[type] || type}</span>
    </Badge>
  );
};

const StatusBadge = ({ status }) => {
  const styles = {
    pending: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
    settled: "bg-green-500/20 text-green-400 border-green-500/30",
    reconciled: "bg-blue-500/20 text-blue-400 border-blue-500/30"
  };
  const icons = {
    pending: Clock,
    settled: CheckCircle2,
    reconciled: CheckCircle2
  };
  const Icon = icons[status] || Clock;
  
  return (
    <Badge variant="outline" className={styles[status] || styles.pending}>
      <Icon className="h-3 w-3 mr-1" />
      {status?.charAt(0).toUpperCase() + status?.slice(1)}
    </Badge>
  );
};

const SettlementLedger = () => {
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("summary");
  
  // Filters
  const [filterMonth, setFilterMonth] = useState(new Date().toISOString().slice(0, 7));
  const [filterPartyType, setFilterPartyType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  
  // Bulk settle modal
  const [isBulkSettleOpen, setIsBulkSettleOpen] = useState(false);
  const [selectedParty, setSelectedParty] = useState(null);

  useEffect(() => {
    fetchData();
  }, [filterMonth, filterPartyType, filterStatus]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterMonth) params.settlement_month = filterMonth;
      if (filterPartyType !== "all") params.party_type = filterPartyType;
      if (filterStatus !== "all") params.status = filterStatus;
      
      const [entriesRes, summaryRes] = await Promise.all([
        getSettlementLedger(params),
        getSettlementSummary({ settlement_month: filterMonth })
      ]);
      
      setEntries(entriesRes.data || []);
      setSummary(summaryRes.data || []);
    } catch (error) {
      toast.error("Failed to load settlement ledger");
    } finally {
      setLoading(false);
    }
  };

  const handleSettleEntry = async (entryId) => {
    try {
      await settleEntry(entryId);
      toast.success("Entry marked as settled");
      fetchData();
    } catch (error) {
      toast.error("Failed to settle entry");
    }
  };

  const openBulkSettleModal = (party) => {
    setSelectedParty(party);
    setIsBulkSettleOpen(true);
  };

  const handleBulkSettle = async () => {
    if (!selectedParty) return;
    
    try {
      await bulkSettle({
        party_type: selectedParty.party_type,
        party_id: selectedParty.party_id,
        settlement_month: filterMonth
      });
      toast.success("All entries settled successfully");
      setIsBulkSettleOpen(false);
      setSelectedParty(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to bulk settle");
    }
  };

  // Calculate totals
  const platformCredits = summary.find(s => s.party_type === "platform")?.total_credits || 0;
  const totalDebits = summary.filter(s => s.party_type !== "platform").reduce((sum, s) => sum + s.total_debits, 0);
  const pendingCount = entries.filter(e => e.status === "pending").length;
  const settledCount = entries.filter(e => e.status === "settled").length;

  // Generate month options (last 12 months)
  const monthOptions = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = d.toISOString().slice(0, 7);
    const label = d.toLocaleDateString('en-ZA', { month: 'long', year: 'numeric' });
    monthOptions.push({ value, label });
  }

  return (
    <div className="space-y-6" data-testid="settlement-ledger-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-2xl">Settlement Ledger</h1>
          <p className="text-muted-foreground">Automatic ledger entries from completed transactions</p>
        </div>
        
        <Button variant="outline" onClick={fetchData} data-testid="refresh-ledger-btn">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-green-500/20">
                <TrendingUp className="h-4 w-4 text-green-400" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Platform Revenue</p>
                <p className="text-xl font-bold font-mono text-green-400">{formatCurrency(platformCredits)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-orange-500/20">
                <TrendingDown className="h-4 w-4 text-orange-400" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Counterparty Debits</p>
                <p className="text-xl font-bold font-mono">{formatCurrency(totalDebits)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-yellow-500/20">
                <Clock className="h-4 w-4 text-yellow-400" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Pending</p>
                <p className="text-xl font-bold font-mono">{pendingCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-green-500/20">
                <CheckCircle2 className="h-4 w-4 text-green-400" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Settled</p>
                <p className="text-xl font-bold font-mono">{settledCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-card border-border">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <Label className="text-sm">Month:</Label>
              <Select value={filterMonth} onValueChange={setFilterMonth}>
                <SelectTrigger className="w-[180px]" data-testid="filter-month">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {monthOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm">Party Type:</Label>
              <Select value={filterPartyType} onValueChange={setFilterPartyType}>
                <SelectTrigger className="w-[160px]" data-testid="filter-party-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="bank">Banks</SelectItem>
                  <SelectItem value="retailer">Retailers</SelectItem>
                  <SelectItem value="atm_acquirer">ATM Acquirers</SelectItem>
                  <SelectItem value="platform">Platform</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm">Status:</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[140px]" data-testid="filter-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="settled">Settled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-[400px] grid-cols-2">
          <TabsTrigger value="summary" data-testid="tab-summary">Summary by Party</TabsTrigger>
          <TabsTrigger value="entries" data-testid="tab-entries">All Entries</TabsTrigger>
        </TabsList>

        {/* Summary Tab */}
        <TabsContent value="summary" className="mt-4">
          <Card className="bg-card border-border">
            <CardContent className="p-0">
              <Table className="data-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Party</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Debits (Owed)</TableHead>
                    <TableHead className="text-right">Credits (Earned)</TableHead>
                    <TableHead className="text-right">VAT</TableHead>
                    <TableHead className="text-right">Net Position</TableHead>
                    <TableHead className="text-center">Transactions</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8">
                        <RefreshCw className="h-6 w-6 animate-spin mx-auto" />
                      </TableCell>
                    </TableRow>
                  ) : summary.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8">
                        <DollarSign className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                        <p className="text-muted-foreground">No ledger entries for this period</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Entries are created automatically when transactions are completed
                        </p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    summary.map((item, idx) => (
                      <TableRow key={idx} data-testid={`summary-row-${item.party_id}`}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <PartyTypeIcon type={item.party_type} />
                            <div>
                              <p className="font-medium">{item.party_name}</p>
                              <p className="text-xs text-muted-foreground">{item.party_id}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <PartyTypeBadge type={item.party_type} />
                        </TableCell>
                        <TableCell className="text-right">
                          {item.total_debits > 0 ? (
                            <span className="font-mono text-red-400">{formatCurrency(item.total_debits)}</span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {item.total_credits > 0 ? (
                            <span className="font-mono text-green-400">{formatCurrency(item.total_credits)}</span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="font-mono text-sm">{formatCurrency(item.vat_total)}</span>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className={`font-mono font-medium ${item.net_position >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {item.net_position >= 0 ? '+' : ''}{formatCurrency(item.net_position)}
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="font-mono">{item.transaction_count}</span>
                        </TableCell>
                        <TableCell className="text-right">
                          {item.party_type !== "platform" && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openBulkSettleModal(item)}
                              data-testid={`settle-${item.party_id}`}
                            >
                              <CheckCircle2 className="h-4 w-4 mr-1" />
                              Settle All
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Entries Tab */}
        <TabsContent value="entries" className="mt-4">
          <Card className="bg-card border-border">
            <CardContent className="p-0">
              <Table className="data-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Party</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">VAT</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8">
                        <RefreshCw className="h-6 w-6 animate-spin mx-auto" />
                      </TableCell>
                    </TableRow>
                  ) : entries.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8">
                        <FileText className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                        <p className="text-muted-foreground">No ledger entries found</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    entries.map((entry) => (
                      <TableRow key={entry.id} data-testid={`entry-row-${entry.id}`}>
                        <TableCell>
                          <span className="font-mono text-sm">{formatDate(entry.settlement_date)}</span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <PartyTypeIcon type={entry.party_type} />
                            <div>
                              <p className="font-medium text-sm">{entry.party_name}</p>
                              <p className="text-xs text-muted-foreground">{entry.party_id}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <p className="text-sm">{entry.description}</p>
                          <p className="text-xs text-muted-foreground font-mono">{entry.transaction_id.slice(0, 8)}...</p>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={entry.entry_type === "credit" ? "text-green-400" : "text-red-400"}>
                            {entry.entry_type === "credit" ? (
                              <ArrowUpRight className="h-3 w-3 mr-1" />
                            ) : (
                              <ArrowDownRight className="h-3 w-3 mr-1" />
                            )}
                            {entry.entry_type.charAt(0).toUpperCase() + entry.entry_type.slice(1)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className={`font-mono ${entry.entry_type === "credit" ? "text-green-400" : "text-red-400"}`}>
                            {entry.entry_type === "credit" ? "+" : "-"}{formatCurrency(entry.amount)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="font-mono text-sm">{formatCurrency(entry.vat_amount)}</span>
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={entry.status} />
                        </TableCell>
                        <TableCell className="text-right">
                          {entry.status === "pending" && entry.party_type !== "platform" && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleSettleEntry(entry.id)}
                              data-testid={`settle-entry-${entry.id}`}
                            >
                              <CheckCircle2 className="h-4 w-4 text-green-400" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Info Card */}
      <Card className="bg-secondary/30 border-border">
        <CardContent className="p-6">
          <h3 className="font-heading font-semibold mb-3">How Settlement Ledger Works</h3>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <ChevronRight className="h-4 w-4 mt-0.5 text-primary" />
              <span>Ledger entries are <strong>automatically created</strong> when transactions are completed (Redeemed status)</span>
            </li>
            <li className="flex items-start gap-2">
              <ChevronRight className="h-4 w-4 mt-0.5 text-primary" />
              <span><strong>Debit entries</strong> represent fees owed by counterparties (banks, retailers, ATM switches)</span>
            </li>
            <li className="flex items-start gap-2">
              <ChevronRight className="h-4 w-4 mt-0.5 text-primary" />
              <span><strong>Credit entries</strong> represent platform revenue (Motswagae fee)</span>
            </li>
            <li className="flex items-start gap-2">
              <ChevronRight className="h-4 w-4 mt-0.5 text-primary" />
              <span>Fee splits are calculated based on <strong>active Fee Schedules</strong> configured for each counterparty</span>
            </li>
            <li className="flex items-start gap-2">
              <ChevronRight className="h-4 w-4 mt-0.5 text-primary" />
              <span>Use <strong>"Settle All"</strong> after payment received to mark entries as settled</span>
            </li>
          </ul>
        </CardContent>
      </Card>

      {/* Bulk Settle Modal */}
      <Dialog open={isBulkSettleOpen} onOpenChange={(open) => {
        setIsBulkSettleOpen(open);
        if (!open) setSelectedParty(null);
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Settle All Entries</DialogTitle>
            <DialogDescription>
              Mark all pending entries for {selectedParty?.party_name} as settled
            </DialogDescription>
          </DialogHeader>
          
          {selectedParty && (
            <div className="py-4 space-y-4">
              <div className="bg-secondary/50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Party</span>
                  <span>{selectedParty.party_name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Type</span>
                  <PartyTypeBadge type={selectedParty.party_type} />
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Month</span>
                  <span>{filterMonth}</span>
                </div>
                <Separator />
                <div className="flex justify-between text-sm font-medium">
                  <span>Amount to Settle</span>
                  <span className="text-red-400">{formatCurrency(selectedParty.total_debits)}</span>
                </div>
              </div>
              
              <p className="text-sm text-muted-foreground">
                This will mark all pending ledger entries for this party as "settled". 
                Use this after confirming payment has been received.
              </p>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsBulkSettleOpen(false)}>Cancel</Button>
            <Button onClick={handleBulkSettle} className="bg-green-600 hover:bg-green-700">
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Confirm Settlement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SettlementLedger;
